import React, { useMemo, useState } from "react";

import Panel from "../../../../../../features/panel-simple/components/panel/Panel";
import RoundedTabs from "../../../../../../components/ui/tabs/rounded-tabs/RoundedTabs";
import RemoveFromLibrary from "../../../../../../components/ui/add-to-library-button/RemoveFromLibrary";
import DeleteTemplateModal from "../../DeleteTemplateModal";
import { usePanelSimpleStore } from "../../../../../../stores/use-panel-simple-store";
import { useOpticsStore } from "../../../../../../stores/use-optics-store";
import {
  OverviewPanelsTemplatesContainer,
  OverviewPanelTemplate,
  PanelsTemplatesContainer,
  PanelTemplate,
  Translate,
} from "./styles";
import { panelLibraryService } from "@Shared/Service/PanelLibraryService";
import { Column } from "../../styles";
import { getPanelTemplates } from "./panel-simple-templates";
import { MACRO_KEY } from "../../../../../defaults";

const getPanelData = (template, panelData) => {
  return {
    ...template,
    title: panelData.title || template.title,
    text: panelData.text || template.text,
  };
};

export const OverviewTemplates = () => {
  const { data: panelData, setData } = usePanelSimpleStore((state) => ({
    data: state.data,
    setData: state.setData,
  }));
  const { setDrawers, styleguideSetup, setMacro, colorPalette } =
    useOpticsStore((state) => ({
      setDrawers: state.setDrawers,
      styleguideSetup: state.styleguideSetup,
      setMacro: state.setMacro,
      colorPalette: state.colorPalette,
    }));

  const templates = useMemo(
    () =>
      getPanelTemplates({
        cornerRadius: styleguideSetup.cornerRadius,
        selectedColors: colorPalette,
        isInOverview: true,
      }),
    [styleguideSetup, colorPalette]
  );

  return (
    <OverviewPanelsTemplatesContainer>
      {templates?.map((data) => (
        <OverviewPanelTemplate
          key={data.id}
          onClick={() => {
            setMacro({ key: MACRO_KEY.PANEL_SIMPLE });
            setData(getPanelData(data, panelData));
            setDrawers({ templates: false, settings: true });
          }}
          isSelected={data.id === panelData.id}
        >
          <Panel data={data} isTemplate={true} />
        </OverviewPanelTemplate>
      ))}
    </OverviewPanelsTemplatesContainer>
  );
};

const FromLibrary = ({ panels = [] }) => {
  const [panelToDelete, setPanelToDelete] = useState();

  const { data: panelData, setData } = usePanelSimpleStore((state) => ({
    data: state.data,
    setData: state.setData,
  }));
  const {
    setDrawers,
    hasStyleguidePermission,
    setLibraryPanels,
    isDrawerModalOpen,
    setIsDrawerModalOpen,
  } = useOpticsStore((state) => ({
    setDrawers: state.setDrawers,
    hasStyleguidePermission: state.hasStyleguidePermission,
    setLibraryPanels: state.setLibraryPanels,
    isDrawerModalOpen: state.isDrawerModalOpen,
    setIsDrawerModalOpen: state.setIsDrawerModalOpen,
  }));

  const removePanelFromLibrary = async (id) => {
    setLibraryPanels([...panels.filter((panel) => panel.id !== id)]);
    try {
      await panelLibraryService.deletePanel(id);
    } catch (error) {
      console.log("Failed to remove panel from library");
    }
  };

  return (
    <>
      <PanelsTemplatesContainer>
        {panels.map((data) => (
          <PanelTemplate
            onClick={() => {
              setData(getPanelData(data, panelData));
              setDrawers({ templates: false, settings: true });
            }}
            key={data.id}
            isSelected={data.id === panelData.id}
          >
            <Translate>
              <Panel data={data} isTemplate={true} />
            </Translate>
            {hasStyleguidePermission && (
              <RemoveFromLibrary
                onClick={(e) => {
                  e.stopPropagation();

                  setIsDrawerModalOpen(true);
                  setPanelToDelete(data.id);
                }}
              />
            )}
          </PanelTemplate>
        ))}
      </PanelsTemplatesContainer>

      <DeleteTemplateModal
        isOpen={isDrawerModalOpen}
        onClose={() => setIsDrawerModalOpen(false)}
        onConfirm={() => removePanelFromLibrary(panelToDelete)}
        macroName="panel"
      />
    </>
  );
};

const BuiltTemplates = ({ selectedTab }) => {
  const { data: panelData, setData } = usePanelSimpleStore((state) => ({
    data: state.data,
    setData: state.setData,
  }));
  const { setDrawers, styleguideSetup, colorPalette } = useOpticsStore(
    (state) => ({
      setDrawers: state.setDrawers,
      styleguideSetup: state.styleguideSetup,
      colorPalette: state.colorPalette,
    })
  );

  const templates = useMemo(
    () =>
      getPanelTemplates({
        cornerRadius: styleguideSetup.cornerRadius,
        selectedColors: colorPalette,
      }),
    [styleguideSetup, colorPalette]
  );

  return (
    <PanelsTemplatesContainer>
      {templates?.[selectedTab]?.map((data) => (
        <PanelTemplate
          key={data.id}
          onClick={() => {
            setData(getPanelData(data, panelData));
            setDrawers({ templates: false, settings: true });
          }}
          isSelected={data.id === panelData.id}
        >
          <Translate>
            <Panel data={data} isTemplate={true} />
          </Translate>
        </PanelTemplate>
      ))}
    </PanelsTemplatesContainer>
  );
};

const Tabs = [
  {
    id: "fl",
    i18n: "from-library",
    label: "From library",
    Content: FromLibrary,
  },
  {
    id: "standard",
    label: "Standard",
    i18n: "standard",
    Content: BuiltTemplates,
  },
  {
    id: "dot",
    label: "Notification",
    i18n: "notification",
    Content: BuiltTemplates,
  },
  {
    id: "custom_icon",
    label: "Custom Icon",
    i18n: "custom-icon",
    Content: BuiltTemplates,
  },
  { id: "emoji", label: "Emoji", i18n: "emoji", Content: BuiltTemplates },
];

const PanelsTemplates = () => {
  const { libraryPanels, selectedTemplateTab, setSelectedTemplateTab } =
    useOpticsStore((state) => ({
      libraryPanels: state.libraryPanels,
      selectedTemplateTab: state.selectedTemplateTab,
      setSelectedTemplateTab: state.setSelectedTemplateTab,
    }));

  const selectedTab = selectedTemplateTab.panel;

  const { Content } = Tabs.find(({ id }) => id === selectedTab);

  return (
    <Column>
      <RoundedTabs
        tabs={Tabs.filter(({ id }) => libraryPanels?.length || id !== "fl")}
        selectedTab={selectedTab}
        setSelectedTab={(value) =>
          setSelectedTemplateTab({ macro: "panel", tab: value })
        }
      />

      <Content panels={libraryPanels} selectedTab={selectedTab} />
    </Column>
  );
};

export default PanelsTemplates;
